import {
  ApartmentOutlined,
  AreaChartOutlined,
  DashboardOutlined,
  DesktopOutlined,
  FileSearchOutlined,
  FundOutlined,
  KeyOutlined,
  MenuFoldOutlined,
  MenuOutlined,
  MenuUnfoldOutlined,
  PieChartOutlined,
  ProjectOutlined,
  SafetyCertificateOutlined,
  ScheduleOutlined
} from '@ant-design/icons';
import { UserMenu } from '@hq/account';
import { hasPermission } from '@hq/permission';
import { truncateByWidth } from '@hq/utils';
import { Button, Drawer, Layout, Menu, Space, Tag, Typography } from 'antd';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useIsMobile } from '../components/useMobile';
import { appTitle, hq, useAuthStore } from '../session';

const { Header, Sider, Content } = Layout;

/** 监管端菜单（docs/05 §4.4）。 */
interface MenuItem {
  key: string;
  icon: ReactNode;
  label: string;
  /** 进入该菜单所需权限码（sys_permission.perm_code）；空表示登录即可见。 */
  perm?: string;
}

const ANALYTICS = 'wl:analytics:view';

/** 新窗口打开的全屏大屏路由（不走账号登录态，凭屏端设备凭据独立取数）。 */
const SCREEN_EXT_KEY = 'ext:/screen';

const MENU: MenuItem[] = [
  { key: '/overview', icon: <DashboardOutlined />, label: '全区总览' },
  // 上移到第 2：监管核心动线是「总览发现异常 → 五级穿透逐层追查」，穿透页自带周期/层级/机构筛选，可独立进入
  { key: '/drill', icon: <FileSearchOutlined />, label: '五级穿透', perm: ANALYTICS },
  // 面上分析组：机构排名 / 采购结构 / 预算时效 / 监管专题 / 医共体视角（覆盖主体由宽到窄）
  { key: '/rank', icon: <FundOutlined />, label: '机构排名', perm: ANALYTICS },
  { key: '/structure', icon: <PieChartOutlined />, label: '采购结构', perm: ANALYTICS },
  { key: '/performance', icon: <ScheduleOutlined />, label: '预算时效', perm: ANALYTICS },
  { key: '/topic', icon: <SafetyCertificateOutlined />, label: '监管专题', perm: ANALYTICS },
  { key: '/group', icon: <ApartmentOutlined />, label: '医共体视角', perm: ANALYTICS },
  // 周期复核（本期/上期/去年同期对比，非日常浏览流），从分析组中段下沉
  { key: '/report', icon: <AreaChartOutlined />, label: '报表订阅', perm: ANALYTICS },
  // 大屏：全屏展示（新窗口）+ 本机构列表 + 申请码确认（输入屏上 4-4-4 三段申请码），操作型工具成组置于末尾
  { key: SCREEN_EXT_KEY, icon: <ProjectOutlined />, label: '打开大屏', perm: ANALYTICS },
  { key: '/screen-devices', icon: <DesktopOutlined />, label: '我的大屏', perm: ANALYTICS },
  { key: '/screen-scan', icon: <KeyOutlined />, label: '申请码确认', perm: ANALYTICS }
];

export default function AppLayout() {
  const navigate = useNavigate();
  const location = useLocation();
  const profile = useAuthStore((state) => state.profile);
  const patchProfile = useAuthStore((state) => state.patchProfile);
  const clear = useAuthStore((state) => state.clear);
  // 侧栏受控折叠：底部用简化图标按钮替代默认整条 trigger
  const [collapsed, setCollapsed] = useState(false);
  // 移动端：<768px 侧栏改为抽屉，菜单选中后自动收起
  const isMobile = useIsMobile();
  const [drawerOpen, setDrawerOpen] = useState(false);

  // 侧栏顶部展示租户简称；旧会话与开发态发号缺该字段时，用 /auth/me 补一次
  useEffect(() => {
    if (!profile || profile.tenantName !== undefined) return;
    let cancelled = false;
    hq.auth
      .me()
      .then((me) => {
        if (!cancelled) patchProfile({ tenantName: me.tenantName ?? null });
      })
      .catch(() => undefined);
    return () => {
      cancelled = true;
    };
  }, [profile, patchProfile]);

  const tenantTitle = profile?.tenantName || appTitle;

  // 菜单按后端权限码裁剪：管理员不裁剪；无权限快照的会话按 fail-closed 处理（只显示无需权限的菜单）
  const menuItems = useMemo(
    () =>
      MENU.filter((item) => {
        if (!item.perm) return true;
        if (profile?.level === 'admin') return true;
        if (!profile?.perms) return false;
        return hasPermission(profile.perms, item.perm);
      }).map((item) => ({ key: item.key, icon: item.icon, label: item.label })),
    [profile?.level, profile?.perms]
  );

  const selected =
    menuItems.find((item) => location.pathname === item.key)?.key ??
    menuItems.find((item) => location.pathname.startsWith(`${item.key}/`))?.key ??
    (location.pathname === '/screen' ? '' : '/overview');

  // 移动端切换菜单后自动收起抽屉
  useEffect(() => {
    if (isMobile) setDrawerOpen(false);
  }, [selected, isMobile]);

  // 侧栏内容（logo + 菜单）：桌面放 Sider，移动端放 Drawer，两端复用同一份
  const siderContent = (
    <>
      <div className="app-logo">
        <img className="app-logo__img" src="/logo.png" alt="logo" />
        {!collapsed && <span title={tenantTitle}>{truncateByWidth(tenantTitle)}</span>}
      </div>
      <div className="app-sider__menu">
        <Menu
          theme="dark"
          mode="inline"
          selectedKeys={[selected]}
          items={menuItems}
          onClick={({ key }) =>
            key === SCREEN_EXT_KEY ? window.open('/screen', '_blank') : navigate(key)
          }
        />
      </div>
    </>
  );

  return (
    <Layout style={{ minHeight: '100vh' }}>
      {isMobile ? (
        <Drawer
          placement="left"
          width={232}
          open={drawerOpen}
          onClose={() => setDrawerOpen(false)}
          closable={false}
          classNames={{ body: 'app-drawer__body' }}
          styles={{ body: { padding: 0, background: '#001529' } }}
        >
          {siderContent}
        </Drawer>
      ) : (
        <Sider
          className="app-sider"
          theme="dark"
          width={208}
          collapsible
          collapsed={collapsed}
          onCollapse={setCollapsed}
          trigger={null}
          style={{ position: 'sticky', top: 0, height: '100vh' }}
        >
          {siderContent}
        </Sider>
      )}
      <Layout>
        <Header
          style={{
            background: '#fff',
            paddingInline: isMobile ? 12 : 24,
            // 不能继承 antd Header 的 64px 行高：ellipsis 单行文本是 inline-block + vertical-align: bottom，
            // 会被行盒压到最底部，姓名与「退出」就错行了；行高交回 normal 由 flex 居中
            lineHeight: 'normal',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between'
          }}
        >
          <Space>
            {/* 收起/展开：置顶栏左侧，贴近导航栏；移动端改为抽屉开关 */}
            <Button
              type="text"
              style={{ marginInlineStart: -12 }}
              icon={isMobile ? <MenuOutlined /> : collapsed ? <MenuUnfoldOutlined /> : <MenuFoldOutlined />}
              onClick={() => (isMobile ? setDrawerOpen(true) : setCollapsed(!collapsed))}
            />
            {!isMobile && <Typography.Text type="secondary">监管端 · 只读视角</Typography.Text>}
            <Tag color="purple">GOV 门户</Tag>
          </Space>
          <Space size={isMobile ? 8 : 16} style={{ flexShrink: isMobile ? 1 : 0, minWidth: 0, whiteSpace: 'nowrap' }}>
            {profile ? (
              <UserMenu
                hq={hq}
                compact={isMobile}
                subtitle={profile.tenantName ?? profile.orgName ?? undefined}
                profile={{
                  userId: profile.userId,
                  userCode: profile.userCode,
                  name: profile.name,
                  orgName: profile.orgName,
                  deptName: profile.deptName,
                  mobile: profile.mobile,
                  email: profile.email,
                  avatarUrl: profile.avatarUrl,
                  level: profile.level,
                  levelText: profile.level
                }}
                onProfileUpdated={(next) =>
                  patchProfile({
                    avatarUrl: next.avatarUrl,
                    deptName: next.deptName,
                    mobile: next.mobile,
                    email: next.email,
                    userCode: next.userCode,
                    orgName: next.orgName ?? profile.orgName
                  })
                }
                onLogout={() => {
                  clear();
                  navigate('/login', { replace: true });
                }}
              />
            ) : (
              <Typography.Text type="secondary">未登录</Typography.Text>
            )}
          </Space>
        </Header>
        <Content style={{ padding: isMobile ? 12 : 24 }}>
          <Outlet />
        </Content>
      </Layout>
    </Layout>
  );
}
